<script lang="ts">
    import CardWrapper from "../lib/components/CardWrapper.svelte"

    import CollapsibleWrapper from "../lib/components/CollapsibleWrapper.svelte"

    import Cta from "../lib/components/CTA.svelte"
    import { deleteDBEntry, getDBEntries } from "../api"
    import { formatDate } from "../lib/components/table/fields/DateTime.svelte"
    import TaskTable from "../lib/components/table/TaskTable.svelte"
    import { actionApproval, login, newNotification } from "../lib/store"
    import { spaNavigate } from "../lib/actions"
    import OrderFilter from "../lib/components/OrderFilter.svelte"
    import CreateOrderForm from "../lib/components/CreateOrderForm.svelte"
    import { addTaskToOrder } from "../lib/utils"

    // increments per loadData call: stale (overlapped) responses are discarded
    let loadSeq = 0

    async function loadData(filter: OrderFilterT = {}) {
        const seq = ++loadSeq
        loading = true
        // snapshot: concurrent calls must not share/shift the backend offset
        const myOffset = offset
        // @ts-ignore
        // fetch one extra entry: filtering below (e.g. canceled orders) can
        // shrink the page, so `fetched.length > limit` is the only reliable
        // "more available" signal
        const fetched: Order[] = await getDBEntries("order", filter, "-insertTime", limit + 1, myOffset, null, true)
        if (seq !== loadSeq) return null
        showMoreButton = fetched.length > limit
        const orders: Order[] = fetched.slice(0, limit)
        // make sure every order
        // @ts-ignore
        const tasks: (TaskOilSpillRemoval | TaskTowing)[] = await getDBEntries("task", {
            orderId: { $in: orders.map((order) => order.id) },
        })
        if (seq !== loadSeq) return null

        let orderList = orders.map((order: Order) => {
            const filteredTasks: (TaskOilSpillRemoval | TaskTowing)[] = tasks.filter(
                (task) => task.orderId === order.id
            )
            return {
                order,
                tasks: filteredTasks,
            }
        })
        if (filter.status == "canceled")
            orderList = orderList.filter((order) => order.order.generalDetails.status == "canceled")
        else orderList = orderList.filter((order) => order.order.generalDetails.status != "canceled")

        // advance by unfiltered count only on success: client-side filtering
        // above must not shift the backend offset, or entries repeat/skip
        offset = myOffset + orders.length
        loading = false
        return orderList
    }

    function loadDataFromZero() {
        offset = 0
        loadData(filter).then((data) => {
            if (data) orderList = data
        })
    }

    async function deleteOrder(orderId: string) {
        const connectedTasks = await getDBEntries("task", { orderId })
        for (const task of connectedTasks) {
            await deleteDBEntry("task", task.id)
        }
        await deleteDBEntry("order", orderId).then(() => {
            newNotification({
                html: "Auftrag und verbundene Einsätze gelöscht.",
                class: "success",
            })
            loadDataFromZero()
        })
    }
    let filter: OrderFilterT = {
        status: "all",
        taskType: "all",
        date: null,
        textSearch: "",
    }
    let limit = 10
    let offset = 0
    let refreshOrderList = false
    let orderList: {
        order: Order
        tasks: (TaskOilSpillRemoval | TaskTowing)[]
    }[] = []

    let loading = true

    function reset() {
        offset = 0
        orderList = []
        loadData(filter).then((data) => {
            if (data) orderList = data
        })
    }
    let showMoreButton = true
    // initial load via reactive statement below (fires on mount)
    $: if (filter) reset()
</script>

{#if ($login?.user?.additionalPermissions || []).includes("createOrders") || $login?.user?.roles.includes("admin")}
    <section class="set-order wrapper">
        <CreateOrderForm on:updateOrderList="{loadDataFromZero}" />
    </section>
{/if}

<section class="orderList wrapper">
    <CardWrapper
        icon="solar_clipboard-text-outline.svg"
        title="Auftragsliste"
        noMargin="{true}"
    >
        <OrderFilter bind:filter="{filter}" />

        {#key refreshOrderList}
            {#if orderList.length === 0 && !loading}
                <p style="padding: 3.6rem 0px;">Keine Aufträge gefunden.</p>
            {:else}
                {#each orderList as { order, tasks } (order.id)}
                    <CollapsibleWrapper
                        showOrderIcon="{order.generalDetails.status}"
                        content="{[
                            `Auftrags-Nr. ${order.generalDetails.number}`,
                            formatDate(new Date(order.insertTime), true),
                            order.generalDetails.caller.name,
                            order.generalDetails.caller.companyName,
                        ]}"
                        on:openOrder="{() => {
                            spaNavigate(`/order/${order.id}`)
                        }}"
                    >
                        <div
                            slot="actionButtons"
                            class="actionButtons"
                        >
                            {#if $login.user.roles.includes("admin")}
                                <Cta
                                    type="tableButton"
                                    icon="solar_trash-bin-trash-outline"
                                    on:click="{() => {
                                        if (!$login.user.roles.includes('admin')) {
                                            newNotification({
                                                html: 'Nur Administratoren können Aufträge löschen.',
                                                class: 'error',
                                            })
                                            return
                                        }
                                        $actionApproval = {
                                            modalTitle: 'Auftrag löschen',
                                            modalText: 'Möchten Sie den Auftrag wirklich samt Einsätze löschen?',
                                            callback: () => {
                                                deleteOrder(order.id)
                                            },
                                        }
                                    }}"
                                />
                            {/if}
                            {#if $login.user.roles.includes("admin") || order.generalDetails.status == "open" || order.generalDetails.status == "inProgress"}
                                <Cta
                                    type="tableButton"
                                    icon="solar_pen-outline"
                                    on:click="{() => {
                                        spaNavigate(`/order/${order.id}`)
                                    }}"
                                />
                            {/if}
                            {#if $login.user.roles.includes("admin") || order.generalDetails.status == "open" || order.generalDetails.status == "inProgress"}
                                <Cta
                                    type="tableButton"
                                    icon="mage_plus"
                                    on:click="{() => {
                                        //@ts-ignore
                                        $actionApproval = {
                                            modalTitle: 'Einsatz hinzufügen',
                                            modalText: `Möchten Sie einen neuen Einsatz zum Auftrag Nr. ${order.generalDetails.number} hinzufügen?`,
                                            callback: () => addTaskToOrder(order.id),
                                        }
                                    }}"
                                />
                            {/if}
                        </div>
                        <TaskTable
                            rows="{tasks}"
                            on:click="{(e) => {
                                const task = e.detail
                                $actionApproval = {
                                    modalTitle: 'Einsatz öffnen',
                                    modalText: `Sind Sie sicher, dass Sie den Einsatz "${task.generalDetails.shortDescription}" gehörend zum Auftrag Nr. ${order.generalDetails.number} öffnen möchten? Prüfen Sie, ob Sie zugewiesen wurden bzw. ob sie die Erlaubnis haben. Alle änderungen werden Protokolliert!`,
                                    callback: () => spaNavigate(`/order/${order.id}/task/${task.id}`),
                                }
                            }}"
                        />
                    </CollapsibleWrapper>
                {/each}
            {/if}
            {#if showMoreButton && !loading}
                <!-- Load more -->
                <div
                    class="centerbutton"
                    style="margin-bottom: 2.4rem;"
                >
                    <Cta
                        type="BubbleButton"
                        colorForce="#ADB5BD"
                        content="mehr laden"
                        on:click="{() =>
                            loadData(filter).then((data) => {
                                if (data) {
                                    orderList.push(...data)
                                    orderList = orderList
                                }
                            })}"
                    />
                </div>
            {/if}
        {/key}
    </CardWrapper>
</section>

<style lang="less">
    @import "../../assets/variables.less";
    .set-order {
        margin: 2.4rem 0px;
    }
    .orderList {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 1.2rem;
        margin-bottom: 2.4rem;
    }
    .loadmore {
        margin-top: 40px;
        border-radius: 20px;
        border: solid 1px #ccc;
        color: var(--text-100);
    }
</style>
